import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, Printer, FileDoc, ForkKnife } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import FizickoLiceAutocomplete from '@/components/FizickoLiceAutocomplete';
import ClientAutocomplete from '../components/ClientAutocomplete';

export default function UgostiteljskaPage() {
  const [zahtjevi, setZahtjevi] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    vrsta_zahtjeva: 'ugostiteljska_djelatnost',
    client_id: '',
    datum_podnosenja: new Date().toISOString().split('T')[0],
    podnosilac_naziv: '', podnosilac_adresa: '', podnosilac_pib: '', podnosilac_kontakt: '',
    ovlasceno_lice: '', mjesto_podnosenja: '',
    ug_vrsta_djelatnosti: '', ug_naziv_subjekta: '', ug_adresa_subjekta: '',
    ug_pib: '', ug_crps: '', ug_objekat_naziv_vrsta_adresa: '',
    ug_kontakt_telefon: '', ug_ovlasceno_lice: '', ug_grad: 'Podgorica',
    napomena: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [z, c] = await Promise.all([api.get('/zahtjevi?vrsta=ugostiteljska_djelatnost'), api.get('/clients')]);
      setZahtjevi(z.data); setClients(c.data);
    } catch { toast.error('Greška pri učitavanju'); } finally { setLoading(false); }
  };

  const handleClientSelect = (cid) => {
    const c = clients.find(cl => cl.id === cid);
    if (c) setForm(prev => ({
      ...prev, client_id: cid,
      ug_naziv_subjekta: c.naziv_firme || c.skraceni_naziv || '',
      ug_adresa_subjekta: [c.adresa, c.grad].filter(Boolean).join(', '),
      ug_pib: c.pib || '',
      ug_crps: c.registarski_broj || '',
      ug_kontakt_telefon: [c.telefon, c.email].filter(Boolean).join(', '),
      ug_ovlasceno_lice: c.direktor || '',
      podnosilac_naziv: c.naziv_firme || c.skraceni_naziv || '',
      podnosilac_adresa: [c.adresa, c.grad].filter(Boolean).join(', '),
      podnosilac_pib: c.pib || '',
      podnosilac_kontakt: [c.telefon, c.email].filter(Boolean).join(', '),
    }));
  };

  useEffect(() => {
    if (isFormOpen && form.client_id && clients.length > 0 && !form.ug_naziv_subjekta) {
      handleClientSelect(form.client_id);
    }
  }, [isFormOpen, clients]);

  const handleSubmit = async () => {
    if (!form.ug_vrsta_djelatnosti) { toast.error('Unesite vrstu ugostiteljske djelatnosti'); return; }
    if (!form.ug_naziv_subjekta) { toast.error('Unesite naziv subjekta'); return; }
    try {
      if (editingId) { await api.put(`/zahtjevi/${editingId}`, form); toast.success('Ažurirano'); }
      else { await api.post('/zahtjevi', form); toast.success('Kreirano'); }
      setIsFormOpen(false); setEditingId(null); setForm(emptyForm); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const handleEdit = (z) => {
    setEditingId(z.id);
    setForm({ ...emptyForm, ...z });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi/${id}`); toast.success('Obrisano'); fetchData(); } catch { toast.error('Greška'); }
  };

  const buildHtml = (z) => {
    const today = formatDatePrint(z.datum_podnosenja) || formatDatePrint(new Date().toISOString().split('T')[0]);
    const vrstaDjelatnosti = z.ug_vrsta_djelatnosti || '__________';
    const naziv = z.ug_naziv_subjekta || z.podnosilac_naziv || '__________';
    const adresa = z.ug_adresa_subjekta || z.podnosilac_adresa || '__________';
    const pibCrps = (z.ug_pib || z.ug_crps)
      ? `${z.ug_pib || '__________'}${z.ug_crps ? ' / ' + z.ug_crps : ''}`
      : (z.podnosilac_pib || '__________');
    const objekat = z.ug_objekat_naziv_vrsta_adresa || '__________';
    const kontakt = z.ug_kontakt_telefon || z.podnosilac_kontakt || '__________';
    const ovlasceno = z.ug_ovlasceno_lice || z.ovlasceno_lice || '__________';
    const grad = z.ug_grad || z.mjesto_podnosenja || 'Podgorica';

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Zahtjev - Ministarstvo turizma</title>
<style>
@page { size: A4; margin: 20mm 22mm; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 12pt; color: #000; line-height: 1.55; }
p { margin: 6px 0; text-align: justify; }
.ministry { font-weight: bold; font-size: 12pt; line-height: 1.4; margin-bottom: 2px; }
.ministry-sub { font-size: 11pt; margin-top: 8px; }
.subject { margin: 30px 0 20px; font-weight: bold; }
.body-intro { margin-bottom: 20px; }
.fields { margin-top: 14px; }
.field-row { margin: 10px 0; }
.field-label { font-weight: bold; }
.field-val { border-bottom: 1px solid #000; display: inline-block; min-width: 60%; padding: 0 4px; }
.declaration { margin-top: 26px; font-weight: bold; }
.sig-wrap { display: flex; justify-content: space-between; margin-top: 60px; }
.sig-left, .sig-right { text-align: left; width: 45%; }
.sig-right { text-align: right; }
.sig-label { margin-bottom: 4px; }
.sig-line { border-bottom: 1px solid #000; min-width: 220px; display: inline-block; padding: 2px 6px; }
.mp { margin-top: 20px; font-weight: bold; }
@media print { body { margin: 0; } }
</style></head><body>

<div class="ministry">Ministarstvo turizma, ekologije, održivog<br>razvoja i razvoja sjevera</div>
<div class="ministry-sub"><b>PODGORICA</b><br>Ul. IV proleterske brigade br. 19, Podgorica<br>Crna Gora</div>

<p class="subject">PREDMET: Zahtjev za izdavanje rješenja o odobrenju za obavljanje ugostiteljske djelatnosti</p>

<p class="body-intro">Kao nadležnom organu za postupanje (čl. 73 stav 3 Zakona o turizmu i ugostiteljstvu) obraćamo Vam se zahtjevom za izdavanje rješenja o odobrenju za obavljanje ugostiteljske djelatnosti vrste <b>${vrstaDjelatnosti}</b>.</p>

<div class="fields">
  <div class="field-row"><span class="field-label">Naziv privrednog društva/drugog pravnog lica/preduzetnika/fizičkog lica:</span> <span class="field-val">${naziv}</span></div>
  <div class="field-row"><span class="field-label">Adresa privrednog društva/drugog pravnog lica/preduzetnika/fizičkog lica:</span> <span class="field-val">${adresa}</span></div>
  <div class="field-row"><span class="field-label">PIB i registarski broj iz CRPS:</span> <span class="field-val">${pibCrps}</span></div>
  <div class="field-row"><span class="field-label">Naziv, vrsta i adresa ugostiteljskog objekta:</span> <span class="field-val">${objekat}</span></div>
  <div class="field-row"><span class="field-label">Kontakt telefon lica koje će pratiti postupak utvrđivanja ispunjenosti minimalno tehničkih uslova:</span> <span class="field-val">${kontakt}</span></div>
</div>

<p class="declaration">Uz zahtjev prilažem svu potrebnu dokumentaciju.</p>

<div class="sig-wrap">
  <div class="sig-left">
    <div>Grad: <span class="sig-line">${grad}</span></div>
    <div style="margin-top:10px;">Datum: <span class="sig-line">${today}</span></div>
  </div>
  <div class="sig-right">
    <div class="sig-label">Ime i prezime ovlašćenog lica:</div>
    <div><span class="sig-line">${ovlasceno}</span></div>
    <div style="margin-top:26px;">Potpis: <span class="sig-line"></span></div>
    <div class="mp">M.P.</div>
  </div>
</div>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const handlePrint = (z) => printHTML(buildHtml(z));
  const handleWord = (z) => downloadWord(buildHtml(z), `Ugostiteljska_${(z.ug_naziv_subjekta||z.podnosilac_naziv||'zahtjev').replace(/\s+/g,'_')}.doc`);

  return (
    <div className="space-y-6" data-testid="ugostiteljska-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Ugostiteljska djelatnost</h3>
          <p className="text-sm text-slate-600 mt-1">Zahtjev Ministarstvu turizma za odobrenje ugostiteljske djelatnosti</p>
        </div>
        <Button onClick={() => {setForm(emptyForm);setEditingId(null);setIsFormOpen(true);}} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ug-add-btn">
          <Plus size={20} weight="bold" className="mr-2" /> Novi zahtjev
        </Button>
      </div>
      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : zahtjevi.length === 0 ? (
        <Card className="p-8 text-center">
          <ForkKnife size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Nema zahtjeva za ugostiteljsku djelatnost</p>
        </Card>
      ) : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm text-slate-600" data-testid="ug-table"><thead className="bg-slate-50 border-b"><tr>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Naziv subjekta</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Vrsta djelatnosti</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Objekat</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Formirao</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
        </tr></thead><tbody>{zahtjevi.map(z=>(
          <tr key={z.id} className="border-b hover:bg-slate-50">
            <td className="px-4 py-3 text-xs">{formatDatePrint(z.datum_podnosenja)||'-'}</td>
            <td className="px-4 py-3 font-medium">{z.ug_naziv_subjekta||z.podnosilac_naziv||'-'}</td>
            <td className="px-4 py-3">{z.ug_vrsta_djelatnosti||'-'}</td>
            <td className="px-4 py-3 text-xs text-slate-600">{z.ug_objekat_naziv_vrsta_adresa||'-'}</td>
            <td className="px-4 py-3 text-sm text-slate-600">{z.created_by_name||'-'}</td>
            <td className="px-4 py-3"><div className="flex gap-1">
              <button onClick={()=>handlePrint(z)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600"/></button>
              <button onClick={()=>handleWord(z)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600"/></button>
              <button onClick={()=>handleEdit(z)} className="p-2 hover:bg-slate-200 rounded" title="Uredi"><Pencil size={16} className="text-slate-600"/></button>
              <button onClick={()=>handleDelete(z.id)} className="p-2 hover:bg-red-100 rounded" title="Obriši"><Trash size={16} className="text-red-600"/></button>
            </div></td>
          </tr>))}</tbody></table></div></Card>
      )}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId?'Uredi zahtjev':'Novi zahtjev - Ugostiteljska djelatnost'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <Label className="text-xs font-semibold uppercase text-blue-700 mb-2 block">Auto-popunjavanje iz klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (v=>v!=='none'&&handleClientSelect(v))(id || 'none')} placeholder="Pretraga po nazivu ili PIB-u..." />
            </Card>

            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-500">Vrsta ugostiteljske djelatnosti *</Label>
              <Input value={form.ug_vrsta_djelatnosti} onChange={e=>setForm({...form, ug_vrsta_djelatnosti: e.target.value})} placeholder="npr: restoran, bar, pansion..." className="bg-white border-slate-300 text-sm" data-testid="ug-vrsta-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-500">Naziv privrednog društva / pravnog lica / preduzetnika / fizičkog lica *</Label>
              <Input value={form.ug_naziv_subjekta} onChange={e=>setForm({...form, ug_naziv_subjekta: e.target.value})} className="bg-white border-slate-300 text-sm"/>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-500">Adresa</Label>
              <Input value={form.ug_adresa_subjekta} onChange={e=>setForm({...form, ug_adresa_subjekta: e.target.value})} className="bg-white border-slate-300 text-sm"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase text-slate-500">PIB</Label>
                <Input value={form.ug_pib} onChange={e=>setForm({...form, ug_pib: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase text-slate-500">Registarski broj iz CRPS</Label>
                <Input value={form.ug_crps} onChange={e=>setForm({...form, ug_crps: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-slate-500">Naziv, vrsta i adresa ugostiteljskog objekta *</Label>
              <Input value={form.ug_objekat_naziv_vrsta_adresa} onChange={e=>setForm({...form, ug_objekat_naziv_vrsta_adresa: e.target.value})} placeholder="npr: Restoran 'XYZ', Ul. Slobode 10" className="bg-white border-slate-300 text-sm"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase text-slate-500">Kontakt telefon</Label>
                <Input value={form.ug_kontakt_telefon} onChange={e=>setForm({...form, ug_kontakt_telefon: e.target.value})} placeholder="Lice koje prati postupak MTU" className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase text-slate-500">Ovlašćeno lice</Label>
                <FizickoLiceAutocomplete
                  value={form.ug_ovlasceno_lice}
                  onChange={v => setForm({...form, ug_ovlasceno_lice: v})}
                  onSelect={(fl) => {
                    const name = `${fl.prezime || ''} ${fl.ime || ''}`.trim();
                    setForm(prev => ({ ...prev, ug_ovlasceno_lice: name }));
                    toast.success(`Izabrano: ${name}`);
                  }}
                  clientId={form.client_id}
                  testid="ug-ovlasceno-input"
                /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase text-slate-500">Grad</Label>
                <Input value={form.ug_grad} onChange={e=>setForm({...form, ug_grad: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase text-slate-500">Datum podnošenja</Label>
                <DateInput value={form.datum_podnosenja} onChange={e=>setForm({...form, datum_podnosenja: e.target.value})} className="border-slate-300 text-sm"/></div>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={()=>setIsFormOpen(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">{editingId?'Ažuriraj':'Kreiraj'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
